import type { BatterySimulatorId } from 'constants/batterySimulators'
import type { PERFORMANCE_CALCULATOR_ID } from 'constants/calculators'
import { StdFieldErrorType } from 'reducer/project/projectErrorsReducer'
import type { BillType } from 'types/bills'
import type { ExternalDataType } from 'types/components'
import type { IntegrationData, MountingID, MountingSystemType, PartnerAccessoriesID } from 'types/mounting'
import type { OtherComponentTypes } from 'types/otherComponent'
import type { PaymentOptionDataType } from 'types/paymentOptions'
import type { PricingDataType } from 'types/pricing'
import type { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'
import type {
  MilestonePaymentOverridesMapType,
  PaymentOptionSettingsOverridesType,
  SwedenGreenIncentiveDataType,
} from 'types/systems'

import type { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import {
  HomeTreeApplicationResponseData,
  HomeTreeProjectDetails,
} from 'projectSections/designPanels/StudioErrorPrompts/hometree/types'

export type ComponentOverridesType = {
  [key: string]: {
    price: number
    source: 'unknown' | 'manual' | HardwareSupplierFilterKeyType
  }
}

export interface ThreeObject {
  type: string
  scale: Vector3
  rotation: Vector3
  parent: ThreeObject | undefined
  children: ThreeObject[]
}

export interface Vector3 {
  x: number
  y: number
  z: number

  clone(): Vector3
}

export interface StudioItem<T = unknown> extends ThreeObject {
  uuid: string
  code: string
  authored_by?: string
  applyUserData(): void
  userData: T
  manufacturer_name: string
  getSystem(): StudioSystemType
  quantity?: number
  parent: ThreeObject | undefined
  canDelete?(): boolean
  onDeleteOverride?(): void
  position: Vector3
}
export interface StudioComponentItem<T = unknown, C = unknown> extends StudioItem<T> {
  getComponentData(): C
  code: string
}

declare global {
  class OsInverter {
    constructor(options: { inverter_id: number })
  }

  class OsBattery {
    constructor(options: { battery_id: number })
  }

  class OsString {
    constructor()
  }

  class OsMppt {
    constructor(options?: { groupColorIndex?: number })
    static allocateGroupColorIndex(system: OsSystem | null | undefined): number
  }

  class OsSystem {
    constructor(options?: Record<string, unknown>)
    static getComponentTypeFromSlotKey(slotKey: string): ComponentTypesV2
  }

  class OsTerrain {
    static RUN_DELATIN_ASYNC: boolean
  }
}

type CustomData = {
  [key: string]: any
}

export type LightReachAddersType = {
  backupBatteryCost?: number
  arbitrageBatteryIncluded?: boolean
  backupBatteryIncluded?: boolean
  backupBatteryKwhIncluded?: boolean
  electricalUpgradeIncluded?: boolean
}
export type LightReachIntegrationJson = {
  account_id?: string
  panelManufacturer?: string | undefined
  panelModel?: string | undefined
  storageManufacturer?: string | undefined
  storageModel?: string | undefined
  inverterManufacturer?: string | undefined
  inverterModel?: string | undefined
  mountingManufacturer?: string | undefined
  salesperson_license_number?: string | undefined
  salesRepIdProviderToken?: string | undefined
  prepricing_errors?: string[]
  adders?: LightReachAddersType
}

export type HometreeIntegrationJson = {
  panelManufacturer?: string | undefined
  panelModel?: string | undefined
  storageManufacturer?: string | undefined
  storageModel?: string | undefined
  inverterManufacturer?: string | undefined
  inverterModel?: string | undefined
  mountingManufacturer?: string | undefined
  addressUdprn?: number | undefined
  addressSelectionMode?: 'lookup' | 'manual' | undefined
  addressChoices?: { udprn: number; suggestion: string }[] | undefined
  application?: HomeTreeApplicationResponseData | undefined
  customerApplicationId?: string | undefined
  hasBirdBlocker?: boolean | undefined
  projectDetails?: HomeTreeProjectDetails | undefined
}

export type MountingIntegrationJson = {
  [key in MountingID]?: IntegrationData
}

export type IntegrationJsonType = MountingIntegrationJson & {
  lightreach?: LightReachIntegrationJson
  hometree?: HometreeIntegrationJson
}

declare global {
  class Adder {
    constructor(adder_data: Record<string, any>)
    id: number
    static addToSystem(system: StudioSystemType, adder: Partial<Adder>): void
    static fromArray(items: Record<string, any>[]): Adder[]
    static formulaFromDatabase: Record<number, string>
    static DefaultDataForTests: Record<string, any>
    matchesLineItem(lineItem: AdderData): boolean
  }

  interface Window {
    Adder: typeof Adder
  }
}
export interface AdderData {
  adder_id: number
  category: number
  type: number
  label: string
  value: number
  cost_value: number
  formula: any
  tax_included: number
  show_customer: boolean
  value_includes_tax: boolean
  quantity: number
  unit_label: string | null
  dealer_fee_interaction_type: number
  price_treatment: number
  show_customer_cost: boolean
}

interface OrgSharedTime {
  shared_time: string
  from_org_name: string
  to_org_name: string
}

export interface AdderRecord {
  id: number
  title: string
  url: string
  auto_apply_enabled: boolean
  auto_apply_only_specified_states: string | null
  auto_apply_only_specified_zips: string | null
  auto_apply_component_codes: string | null
  auto_apply_min_system_size: string | null
  auto_apply_max_system_size: string | null
  auto_apply_sector: number
  type: 0 | 1 | 2
  value: number
  formula: 0 | 1 | 2
  tax_included: number
  show_customer: boolean
  cost_value: number
  value_includes_tax: boolean
  unit_label: null
  dealer_fee_interaction_type: number
  price_treatment: number
  show_customer_cost: boolean
  share_with_orgs: string[]
  org: string
  org_id: number
  org_shared_time: OrgSharedTime[]
}

export type EstimationMethod =
  | 'number_panels_and_wattage'
  | 'annual_generation_kwh'
  | 'monthly_generation_kwh'
  | 'bimonthly_generation_kwh'
  | 'quarterly_generation_kwh'
  | 'daily_generation_kwh'
  | 'interval_generation_kwh'

export type ExistingPanelEstimateDetail = {
  panel_count?: number
  panel_rated_power_watts?: number
  group_azimuth?: number
  group_slope_degrees?: number
  total_group_power_kw?: number
}

export type ExistingGenEstimateDetails = {
  estimation_method?: EstimationMethod
  installation_year?: number
  inverter_efficiency?: number
  system_degradation_rate?: number
  shading_factor?: number
  dc_to_ac_ratio?: number
  panels_painted_in_studio?: boolean
  estimated_annual_kwh?: number
  estimated_monthly_kwh?: (number | undefined)[]
  estimated_bimonthly_kwh?: (number | undefined)[]
  estimated_quarterly_kwh?: (number | undefined)[]
  estimated_daily_kwh?: (number | undefined)[]
  // estimated_hourly8760_kwh?: any // this will need to be uploaded by different way, similar to usage
  panel_rated_power_watts?: number
  panel_groups?: ExistingPanelEstimateDetail[]
}

export type ShadeMetricsDataType =
  | { sun_access_factor: number }
  | ({
      items: {
        [key: string]: ShadeMetricsLikeType
      }
    } & ShadeMetricsLikeType)

export interface ShadeMetricsLikeType {
  sun_access_factor: number
  tilt_orientation_factor: number
  total_poa: number
  total_poa_beam: number
  total_poa_beam_shaded: number
  total_poa_diffuse: number
  total_poa_optimal: number
  total_poa_shaded: number
  total_solar_resource_fraction: number
}

export interface StudioOsModuleType extends StudioItem<ModuleType>, ModuleType {
  componentId: number
  getModuleQuantity: () => number
  getModuleGrids: () => ModuleGridType[]
  getComponentSpecs: () => ModuleType
}

export interface StudioSystemOutputType {
  annual: number
  hashed_args: string | undefined
  calculation_errors: StdFieldErrorType[] | []
  shade_metrics?: ShadeMetricsDataType
}

export enum RetrofitSystemDesignChoiceEnum {
  SEPARATE = 'separate',
  COMBINED = 'combined',
}
export type BatteryEOLConfigType = {
  end_of_life_definition: number
  end_of_life_years_override: number | null
  battery_replacement_modelling: number
  battery_replacement_cost_method:
    | 'component_cost_pre_inflation'
    | 'manual_cost_pre_inflation'
    | 'manual_cost_per_unit_pre_inflation'
  battery_replacement_cost_inflation_annual: number
  battery_replacement_cost_value: number | null
}

export type InverterEOLConfigType = {
  inverter_end_of_life_definition?: number
  inverter_end_of_life_years_override?: number | null
  inverter_replacement_modelling: number
  inverter_replacement_cost_method:
    | 'component_cost_pre_inflation'
    | 'manual_cost_pre_inflation'
    | 'manual_cost_per_unit_pre_inflation'
  inverter_replacement_cost_inflation_annual: number
  inverter_replacement_cost_value: number | null
}

export type HardwareEOLConfigType = {
  battery?: BatteryEOLConfigType | null
  inverter?: InverterEOLConfigType | null
}

export type UnstrungModulesByModuleGridType = {
  uuid: string
  modules: ReadonlyArray<{ uuid: string }>
  azimuth: number
  slope: number
  trackingMode?: string
  modulesPerRow?: number
  gcr?: number
  moduleId?: number
  dcOptimizerId?: number
}
export interface StudioSystemType extends StudioItem<StudioSystemDataType> {
  partner_accessories_input: any
  output?: StudioSystemOutputType
  is_current: boolean
  is_simple_generation_estimate: boolean
  system_lifetime: number
  system_efficiency?: number
  id?: number
  system_efficiency_override?: number
  is_battery_dc_connected?: boolean
  is_battery_dc_connected_auto?: boolean
  calculator: PERFORMANCE_CALCULATOR_ID
  battery_simulator_id?: BatterySimulatorId
  moduleId: number
  battery_total_kwh: number
  module_quantity: number
  setModuleTypeByModuleId(moduleId: number, forceUpdate?: boolean): void
  detectModuleId(): number | null
  getCogsOverride(): ComponentOverridesType | undefined
  getLastSelectedOsModuleType(): StudioOsModuleType
  getLastSelectedModuleType(): ModuleType
  hasMultipleModuleTypes(): boolean
  getModuleTypes(): StudioOsModuleType[]
  createModuleType(componentSpecs: ModuleType): void
  moduleType(): ModuleType
  moduleGrids(): ModuleGridType[]
  getModules(): StudioModuleType[]
  children: ModuleGridType[]
  mounting?: MountingID
  mounting_type?: MountingSystemType
  partner_accessories?: PartnerAccessoriesID[]
  integration_json?: IntegrationJsonType
  existing_generation_estimate_details?: ExistingGenEstimateDetails
  retrofit_system_design_choice?: RetrofitSystemDesignChoiceEnum
  reloadModuleSpecs(): void
  inverters(): StudioInverterType[]
  batteries: () => StudioBatteryType[]
  others: () => StudioOtherType[]
  getDcOptimizers: () => StudioOtherType[]
  getDcOptimizerById: (otherId) => StudioOtherType
  addDcOptimizer: (
    otherId: number,
    moduleGridUuids?: string[],
    autoAdded?: boolean,
    commandUUID?: string
  ) => StudioOtherType
  setGlobalDcOptimizer: (otherId: number) => void
  deoptimizeModuleGrids: (moduleGridUuids?: string[]) => void
  dcOptimizer(otherId?: number): OtherType
  milestone_payment_overrides?: MilestonePaymentOverridesMapType
  force_enable_cashflow?: boolean
  lender_milestones_to_use?: string | null
  paymentOptionsData: PaymentOptionDataType[]
  payment_options: PaymentOptionDataType[]
  payment_options_override: number[]
  payment_options_settings_overrides: PaymentOptionSettingsOverridesType
  pricing: PricingDataType
  title: string
  name: string
  hasDistributionBoard?: boolean
  moduleQuantity(): number
  lender_battery_price_override?: number
  load_offsettable_by_battery_fraction?: unknown
  load_offsettable_by_battery_cap?: unknown
  global_reserve_soc_percent?: number | null
  lp_arbitrage_reserve_soc_percent?: number | null
  non_solar_price_included?: number
  show_customer: boolean
  battery_control_scheme: string | null
  utility_tariff_id_override?: number | null
  consumption?: {
    consumption_offset_percentage: number
    grid_independence_percentage_no_battery: number
    self_consumption_percentage_no_battery: number
    usage_annual_proposed?: number
    grid_independence_percentage_with_battery: number
    avg_batt_cycles_per_day?: number | null
    lifetime_soc_min_percent?: number | null
    lifetime_soc_max_percent?: number | null
  }
  line_items: AdderData[]
  allow_shade_mitigation: boolean
  dcOptimizerEfficiency(): number | null
  hasShadingOverride(): boolean
  getName(): string
  kwStc(): number
  batteryTotalKwh(): number
  batteryTotalUsableKwh(): number
  refreshComponentCostOverrides(bomLineItems): void
  refreshModuleTypeQuantities(): void
  compatibleBatteryCodes(): string[]
  compatibleChargerCodes(): string[]
  clearHashedArgs: () => void

  hasBuildablePanels: () => boolean
  allActivePanelsAreBuildable: () => boolean
  setAllPanelsAsBuildable: () => void
  clearBuildablePanels: () => void
  refreshDesignComponentSpecs(): void
  isSpecsReloadRequired(): boolean

  componentCodes(): string[]

  getStrungModules(): StudioModuleType[]
  getUnstrungModules(): StudioModuleType[]
  getUnstrungModuleCount(): number
  unstrungModulesByModuleGrid(): UnstrungModulesByModuleGridType[]

  autoSync?: {
    pricingScheme: boolean
    costing: boolean
    adders: boolean
    commission: boolean
  }

  getComponentsForSlot(slotKey: string): ComponentType[]
  bills: {
    current: BillType
    proposed: Record<string, BillType>
  }
  order: number
  override_price_locking: boolean
  custom_data: CustomData

  inverterRange: string
  unstrungModulesInverterEfficiency: string
  hasIncompleteStringing: Function

  //TODO: should be made private at some point
  assessSlots(): void
  sweden_green_deduction_incentive: SwedenGreenIncentiveDataType
  refreshUserData(): any

  getSystemPanelPlacement(): 'Roof' | 'Ground' | 'Roof & Ground'

  mcs_self_consumption_calculator_override?: number

  weather_dataset_override?: number
  shadingCalcsAwaitingTrigger?: boolean
  raytracedShadingAvailable?: () => boolean
  isAutoCreatedFirstSystem?: boolean
  auto_design_metadata?: Record<string, unknown>
  systemContainsNoComponents?: () => boolean
  pricing_scheme_id?: number
  incentives?: IncentiveStudioItem[]
  costing_override?: number
  commission_id?: number
  hardware_eol_config?: HardwareEOLConfigType
  saveDiffFromPriceOverrideVersusCalculatedPrice(): Record<string, number>
}

export interface StudioSystemDataType {
  shadingByPanelGroup: PanelGroupShading[]
  payment_options: PaymentOptionDataType[]
  slots: any[]
  id: number
  site: {
    is_commercial: boolean
    longitude: number
    latitude: number
    timezoneOffset: number
    zip: string
    country: string
  }
  thumbnail?: string // base64 encoded image

  incentive_to_installer: IncentiveStudioTotal[]
  incentive_to_customer: IncentiveStudioTotal[]
}

export type IncentiveStudioTotal = {
  incentives: IncentiveStudioItem[]
  total: number
  new?: boolean // emphemeral flag to indicate new incentives, gets stripped out before saving
}

// also see: spa/app/src/resources/incentives/IncentiveConstants.ts
export type IncentiveStudioItem = {
  incentive_id: number | null
  incentive_type: number
  title: string
  inc_tax: number
  settings: {
    value: number
    price_based_incentive_type:
      | 'price_including_tax'
      | 'price_excluding_tax'
      | 'price_payable_including_tax'
      | 'price_payable_excluding_tax'
  }
}

export interface PanelGroupShading {
  uuid: string
  slope: number
  azimuth: number
  beam_access: number[]
  module_quantity: number
}
export interface StudioModuleDataType extends ModuleType {}
export interface StudioModuleType extends StudioItem<StudioModuleDataType> {
  parent: ModuleGridType
  cell: string
  active: boolean
  getGrid(): ModuleGridType
}

export interface StudioInverterType extends StudioComponentItem<StudioInverterDataType, InverterComponentType> {
  inverter_type: string
  code: string
  quantity: number
  current_isc_max: number
  current_ac_max?: number
  inverter_id: number
  manufacturer_name: string
  microinverter: boolean
  mppt_voltage_max: number
  mppt_voltage_min: number
  mppt_quantity: number
  voltage_max: number
  voltage_minimum: number
  inbuilt_dc_isolator: string
  mppts(): MpptType[]
  moduleQuantity(): number
  getPanelsPower(): number
  getSystem(): StudioSystemType
  getIsOptimizable(): boolean
  getMpptQuantity(convertZeroQtyToOne?: boolean): number
  getQuantityFromSystemLayout(): number
  isHybridInverter?(): boolean
}
export interface StudioInverterDataType {
  microinverter: unknown
  string_isolation: 'seperately' | 'parallel'
}

export interface StudioBatteryType extends StudioComponentItem<unknown, BatteryComponentType> {
  battery_id: number
  quantity: number
  componentLocked?: boolean
}
export interface StudioOtherType extends StudioComponentItem<unknown, OtherComponentType> {
  slotKey: string
  other_id: null | number
  other_component_type?: OtherComponentTypes
  quantity: number
  componentLocked?: boolean
  external_data?: {
    lyra?: {
      support_status: boolean
      uuid?: string
    }
  }

  // dc_optimizer other_component_type
  dc_optimizer_efficiency: number | null
}
export interface MpptType extends StudioItem {
  parent: StudioInverterType
  strings(): StringType[]
  moduleQuantity(): number
  getPanelsPower(): number
  getSystem(): StudioSystemType
  moduleType(): ModuleType
  getColor(): { getHex(): number; getHexString(): string }
}
export interface StringType extends StudioItem {
  moduleQuantity(): number
  getPanelsPower(): number
  getSystem(): StudioSystemType
  getMppt(): MpptType
  getStrungModuleType(): ModuleType | undefined
  getMppt(): MpptType | null
  getColor(): { getHex(): number; getHexString(): string }
  modules: any[]
}

export interface ComponentType {
  uuid: string
  code: string
  quantity: number
  component_type: ComponentTypes
  other_component_type?: OtherComponentTypes
  external_data: ExternalDataType | null
}
export interface OtherComponentType extends ComponentType {
  compatibleBatteryCodes?: () => string[]
  id: number
  other_id: unknown
  manufacturer_name: string
  show_customer: boolean
}
export interface InverterComponentType extends ComponentType {
  mppt_quantity: number
  compatibleBatteryCodes(): string[]
}
export interface BatteryComponentType extends ComponentType {
  compatibleChargerCodes(): string[]
}

export type PanelPlacement = 'roof' | 'ground'
export type ModuleLayout = 'portrait' | 'landscape'
export type PanelConfiguration = 'STANDARD' | 'TILT_RACK' | 'DUAL_TILT_RACK'
export enum ModuleGridAzimuthalSubsets {
  Front = 'FRONT',
  Back = 'BACK',
  FrontAndBack = 'FRONT_AND_BACK',
}

export type ModuleGridDimensions = {
  width: number
  height: number
  moduleCountX: number
  moduleCountY: number
  moduleSpanX: number
  moduleSpanY: number
}

export interface ModuleGridUserData {
  moduleLayout: ModuleLayout
  slope: number
  azimuth: number
}

export type EMSConfig = {
  showFoundationBlocks: boolean
  height: number
}

export interface ModuleGridType extends StudioItem<ModuleGridUserData> {
  type: 'OsModuleGrid'
  duplicate(options?: { keepPosition?: boolean; select?: boolean }): ModuleGridType
  splitActiveCells(cells: string[], options?: { keepPosition?: boolean; select?: boolean }): ModuleGridType
  getSystem(): StudioSystemType
  getPanelConfiguration(): PanelConfiguration
  getSlope(): number
  getAzimuth(): number
  getGroundClearance(): number
  getModules(): StudioModuleType[]
  getOsModuleType(): StudioOsModuleType
  getRacksTilt(): number
  moduleQuantity(): number
  optionalModuleQuantity(): number
  moduleLayout(setTo?: ModuleLayout): ModuleLayout
  getBounds(): [number, number, number, number]
  getRows(): number
  getCols(): number
  getPanelTilt(): number
  moduleType(): ModuleType
  modulesPerRow(value: number): void
  modulesPerCol(value: number): void
  modulesPerRow(): number
  modulesPerCol(): number
  moduleLayoutOffset(): boolean
  moduleLayoutOffset(value: boolean): void
  trackingMode(): string
  isUsingTiltRacks(): boolean
  getEMSConfig(): EMSConfig | null
  setEMSConfig(emsConfig: EMSConfig | null): void
  calculateGroundCoverageRatio(): number
  getDimensions(): ModuleGridDimensions
  optimize(dcOptimizerId, dispatchEvent?: boolean, isManual?: boolean): number
  deoptimize(dispatchEvent?: boolean, isManual?: boolean): void

  manuallyConfigured: { dcOptimizer: boolean }
  dcOptimizerId?: number
  dcOptimizerLocked?: boolean
  cellsActive: string[]
  name: string
  quantity: number
  cost: number
  slopeAuto: boolean
  azimuthAuto: boolean
  elevationAuto: boolean
  facet: RoofFacetType
  diffuseShading: any
  shadingOverride: any
  panelTiltOverride: number | null
  panelPlacement: 'ground' | 'roof'

  moduleSpacing: [number, number]
  groupSpacing: [number, number]
  moduleSpacingApplied: [number, number]
  groupSpacingApplied: [number, number]

  hasBuildablePanels: () => boolean
  setAllPanelsAsBuildable: () => void
  clearBuildablePanels: () => void
  getCellsActiveModified: (cells: string[], active: boolean) => string[]
  getBuildableCells: () => string[]
  updateBuildableCells: (cells: string[]) => void
  clearInactiveBuildableCellsCache: () => void
}

export interface RoofFacetType {
  isNonSpatial(): boolean
}

export interface EdgeType extends StudioItem {
  getLength(): number
  setLength(lengthInMeters: number): void
  getStructure(): any // OsStructureType does not exist yet
  getFacets(): any[] // OsFacetType does not exist yet
  getSetbackDistance(): number
  setSetbackDistance(distanceInMeters: number): void
  isWire(): boolean
  getLengthConstraint(): number
  hasSetbackDistanceOverride(): boolean
  resetSetbackDistance(): void
  belongsToStructure(): boolean
  isManagedByParent(): boolean
  getCable(): CableType | null
  cableUuid: string | null
  edgeType: string
}

export type CableMaterial = 'copper' | 'aluminium'
export type CableSize = 'auto' | number
export type CableRole =
  | ''
  | 'inverter_msb'
  | 'msb_point_of_supply'
  | 'distribution_board_msb'
  | 'inverter_distribution_board'

export interface CableType extends StudioItem {
  edges: EdgeType[]
  color: string
  cableSize: CableSize | null
  conductorMaterial: CableMaterial | null
  role: CableRole
  inverterUuid: string | null
  lengthOverride: number | null
  getLength(): number
  isComplete(): boolean
  addEdge(edge: EdgeType): boolean
  removeEdge(edge: EdgeType): void
  syncEdgeColors(editor?: unknown): void
}

export interface EdgeTypeStatic {
  wireTypes: readonly string[]
}

export function findObjectSystem(object: StudioItem | ThreeObject): StudioSystemType | undefined {
  if (isStudioItem(object)) {
    return object.getSystem()
  } else {
    let parent = object.parent
    while (parent) {
      if (isStudioSystem(parent)) return parent
      else parent = parent.parent
    }
  }
}
export function isStudioItem(object: StudioItem | ThreeObject): object is StudioItem {
  return object['getSystem'] !== undefined
}
export function isStudioSystem(object: ThreeObject): object is StudioSystemType {
  return object['type'] === 'OsSystem'
}
